// src/runtime/shared/TokenBudgetManager.ts
// ============================================
// TokenBudgetManager - Token 预算管理器（单例）
//
// 职责：
//   1. 控制每日 LLM 调用花费（默认 10 元/日，可配置）
//   2. 控制同一 key 的 LLM 调用频率（默认最小间隔 60 秒）
//   3. 超预算时自动触发降级（canCallLLM 返回 false，由调用方降级到规则化）
//   4. 工作时间限制（默认 08:00-22:00 才允许调用 LLM，非工作时段直接降级）
//   5. 跨日自动重置计数
//
// 设计原则：
//   - 单例模式：所有 Runtime（35 个）共享同一份预算
//   - 进程级状态：当日重启会丢失计数（可接受，因 LLM 调用主要在日间运行）
//   - 非阻塞：超预算时返回 false，由调用方决定如何降级
//   - 线程安全：JavaScript 单线程，无需锁
//
// 价格估算依据（DeepSeek API）：
//   - 输入：约 1 元 / 100 万 token
//   - 输出：约 2 元 / 100 万 token
//   - 混合估算：约 1 元 / 100 万 token（输入+输出加权平均）
//   - 中英文混合：1 token ≈ 3 字符
// ============================================

/** 获取今日日期 YYYY-MM-DD */
function getToday(): string {
  const now = new Date();
  return `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}-${String(
    now.getDate()
  ).padStart(2, '0')}`;
}

/** Token 预算状态 */
export interface TokenBudgetStatus {
  /** 每日预算（元） */
  dailyBudget: number;
  /** 今日已花费（元） */
  spentToday: number;
  /** 今日剩余（元） */
  remaining: number;
  /** 使用率（0-1） */
  usageRate: number;
  /** 是否超预算 */
  overBudget: boolean;
  /** 今日累计调用次数 */
  callCountToday: number;
  /** 当前日期 */
  date: string;
}

/** 默认每日预算（元） */
const DEFAULT_DAILY_BUDGET_YUAN = 10;
/** 默认最小调用间隔（毫秒，同一 key） */
const DEFAULT_MIN_INTERVAL_MS = 60_000;
/** 默认工作开始时间（小时，8 点） */
const DEFAULT_WORK_START_HOUR = 8;
/** 默认工作结束时间（小时，22 点） */
const DEFAULT_WORK_END_HOUR = 22;

/**
 * Token 预算管理器（单例）
 *
 * 全局唯一实例，所有 Runtime 通过 getInstance() 获取
 */
export class TokenBudgetManager {
  private static instance: TokenBudgetManager | null = null;

  /** 每日预算（元） */
  private dailyBudgetYuan: number = DEFAULT_DAILY_BUDGET_YUAN;
  /** 今日已花费（元） */
  private spentTodayYuan: number = 0;
  /** 今日累计调用次数 */
  private callCountToday: number = 0;
  /** 上次重置日期（YYYY-MM-DD） */
  private lastResetDate: string = '';
  /** 工作开始时间（小时） */
  private workStartHour: number = DEFAULT_WORK_START_HOUR;
  /** 工作结束时间（小时） */
  private workEndHour: number = DEFAULT_WORK_END_HOUR;

  /** LLM 调用历史（key -> 时间戳数组，用于频率控制） */
  private callHistory: Map<string, number[]> = new Map();

  /** 日志器（默认 console） */
  private logger: Console = console;

  private constructor() {
    this.lastResetDate = getToday();
  }

  /** 获取单例 */
  static getInstance(): TokenBudgetManager {
    if (!TokenBudgetManager.instance) {
      TokenBudgetManager.instance = new TokenBudgetManager();
    }
    return TokenBudgetManager.instance;
  }

  /**
   * 检查是否可以调用 LLM
   *
   * 双重检查：
   *   1. 预算检查：今日已花费 < 每日预算
   *   2. 频率检查：同一 key 距离上次调用 ≥ minIntervalMs
   *
   * @param key 调用 key（通常为 prompt 哈希或场景标识）
   * @param minIntervalMs 同一 key 最小调用间隔（毫秒，默认 60 秒）
   * @returns true=可调用，false=应降级
   */
  canCallLLM(key: string, minIntervalMs: number = DEFAULT_MIN_INTERVAL_MS): boolean {
    this.checkReset();

    // 0. 工作时间检查（默认 08:00-22:00 才允许调用 LLM）
    const hour = new Date().getHours();
    if (hour < this.workStartHour || hour >= this.workEndHour) {
      this.logger.info(
        `[TokenBudget] 非工作时间（${this.workStartHour}:00-${this.workEndHour}:00），当前 ${hour}:xx，降级到规则化`
      );
      return false;
    }

    // 1. 预算检查
    if (this.spentTodayYuan >= this.dailyBudgetYuan) {
      this.logger.warn(
        `[TokenBudget] 今日 LLM 预算已用尽（¥${this.spentTodayYuan.toFixed(2)}/¥${this.dailyBudgetYuan}），降级到规则化`
      );
      return false;
    }

    // 2. 频率检查（同一 key 至少间隔 minIntervalMs）
    const now = Date.now();
    const history = this.callHistory.get(key) || [];
    const recent = history.filter((t) => now - t < minIntervalMs);
    if (recent.length > 0) {
      this.logger.info(
        `[TokenBudget] 调用频率过高（key=${key.slice(0, 32)}...），降级到缓存或规则化`
      );
      return false;
    }

    return true;
  }

  /**
   * 记录一次 LLM 调用的花费
   *
   * @param yuan 花费金额（元）
   * @param key 调用 key
   */
  recordCost(yuan: number, key: string): void {
    this.checkReset();

    if (yuan <= 0) return;

    this.spentTodayYuan += yuan;
    this.callCountToday += 1;

    // 记录调用时间戳
    const history = this.callHistory.get(key) || [];
    history.push(Date.now());
    // 仅保留最近 100 次（避免内存膨胀）
    if (history.length > 100) {
      history.splice(0, history.length - 100);
    }
    this.callHistory.set(key, history);

    // 接近预算时告警
    const usageRate = this.spentTodayYuan / this.dailyBudgetYuan;
    if (usageRate >= 0.9 && usageRate < 1) {
      this.logger.warn(
        `[TokenBudget] 今日 LLM 预算使用已达 ${(usageRate * 100).toFixed(0)}%（¥${this.spentTodayYuan.toFixed(2)}/¥${this.dailyBudgetYuan}）`
      );
    }
  }

  /**
   * 估算并记录一次调用的花费
   *
   * 基于输入 + 输出字符数粗略估算 token 数与花费
   *
   * @param inputChars 输入字符数（system + user prompt）
   * @param outputChars 输出字符数
   * @param key 调用 key
   */
  estimateAndRecordCost(inputChars: number, outputChars: number, key: string): void {
    // 中英文混合：1 token ≈ 3 字符
    const estimatedTokens = Math.ceil((inputChars + outputChars) / 3);
    // 混合价格：1 元 / 100 万 token
    const estimatedCostYuan = (estimatedTokens / 1_000_000) * 1;
    this.recordCost(estimatedCostYuan, key);
  }

  /**
   * 获取预算状态
   */
  getStatus(): TokenBudgetStatus {
    this.checkReset();
    return {
      dailyBudget: this.dailyBudgetYuan,
      spentToday: this.spentTodayYuan,
      remaining: Math.max(0, this.dailyBudgetYuan - this.spentTodayYuan),
      usageRate: this.dailyBudgetYuan > 0 ? this.spentTodayYuan / this.dailyBudgetYuan : 0,
      overBudget: this.spentTodayYuan >= this.dailyBudgetYuan,
      callCountToday: this.callCountToday,
      date: this.lastResetDate,
    };
  }

  /**
   * 设置每日预算（元）
   *
   * 可通过 EnterpriseBrainService 的 set_token_budget 命令运行时调整
   */
  setDailyBudget(yuan: number): void {
    if (yuan < 0) {
      this.logger.warn(`[TokenBudget] 预算不能为负数：${yuan}，已忽略`);
      return;
    }
    this.dailyBudgetYuan = yuan;
    this.logger.info(`[TokenBudget] 每日预算已设置为 ¥${yuan}`);
  }

  /**
   * 获取当前每日预算
   */
  getDailyBudget(): number {
    return this.dailyBudgetYuan;
  }

  /**
   * 获取今日已花费
   */
  getSpentToday(): number {
    this.checkReset();
    return this.spentTodayYuan;
  }

  /**
   * 重置今日计数（手动）
   *
   * 主要用于测试；正常运行由跨日自动重置处理
   */
  resetToday(): void {
    this.spentTodayYuan = 0;
    this.callCountToday = 0;
    this.callHistory.clear();
    this.lastResetDate = getToday();
    this.logger.info('[TokenBudget] 今日预算计数已重置');
  }

  /**
   * 设置日志器（LLMHelper 等注入时同步日志器）
   */
  setLogger(logger: Console): void {
    this.logger = logger;
  }

  /**
   * 跨日自动重置检查
   *
   * 当检测到日期变更时，自动清零计数
   */
  private checkReset(): void {
    const today = getToday();
    if (today !== this.lastResetDate) {
      this.spentTodayYuan = 0;
      this.callCountToday = 0;
      this.callHistory.clear();
      this.lastResetDate = today;
      this.logger.info(`[TokenBudget] 跨日重置：${today}，预算恢复 ¥${this.dailyBudgetYuan}`);
    }
  }
}

/** 默认导出单例 */
export default TokenBudgetManager.getInstance();
